// useVoiceAgent — browser side of the voice copilot.
//
// Capture: AudioWorklet downsamples the mic to 24 kHz mono and emits PCM16
// frames (~85 ms) which we send as binary WS frames. Playback: the relay streams
// base64 PCM16 @ 24 kHz which we decode to Float32 and schedule gaplessly on a
// playhead cursor (through a gain node, so the speaker can be muted without
// touching the session). Barge-in: the relay sends {type:'interrupted'} on the
// user's speech_started; we flush every queued source so the agent goes silent
// at once. An unexpected socket drop mid-session auto-reconnects (fresh token)
// up to twice before giving up — the mic pipeline stays alive across retries.
import { useCallback, useEffect, useRef, useState } from 'react';
import { agentBus, executeDirective } from '../agent/agentBus';

const API = process.env.REACT_APP_UPLOAD_API || '';
const OUTPUT_RATE = 24000; // Grok streams PCM16 @ 24 kHz; AudioBuffer carries its
// own rate so the context resamples on playback regardless of the device rate.
const MAX_RECONNECTS = 2;

// Inlined AudioWorklet (no static file needed): batches ~2048 samples and posts
// Int16 PCM. Mic is captured at the AudioContext rate (24k), so no resampling.
const WORKLET_SRC = `
class CapProcessor extends AudioWorkletProcessor {
  constructor() { super(); this.buf = []; this.count = 0; this.target = 2048; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) {
      this.buf.push(new Float32Array(ch));
      this.count += ch.length;
      if (this.count >= this.target) {
        const merged = new Float32Array(this.count);
        let o = 0; for (const b of this.buf) { merged.set(b, o); o += b.length; }
        const pcm = new Int16Array(merged.length);
        for (let i = 0; i < merged.length; i++) { let s = Math.max(-1, Math.min(1, merged[i])); pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff; }
        this.port.postMessage(pcm.buffer, [pcm.buffer]);
        this.buf = []; this.count = 0;
      }
    }
    return true;
  }
}
registerProcessor('cap-processor', CapProcessor);
`;

function wsUrl(token) {
  const base = API.replace(/^http/, 'ws').replace(/\/$/, '');
  return `${base}/voice?token=${encodeURIComponent(token || '')}`;
}

function b64ToInt16(b64) {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Int16Array(bytes.buffer, 0, len >> 1);
}

export function useVoiceAgent({ getToken, getInit, onUserFinal, onAssistantFinal }) {
  const [status, setStatus] = useState('idle'); // idle | connecting | live | error
  const [error, setError] = useState('');
  const [level, setLevel] = useState(0); // mic RMS 0..1
  const [userText, setUserText] = useState('');
  const [assistantText, setAssistantText] = useState('');
  const [micMuted, setMicMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);

  const wsRef = useRef(null);
  const ctxRef = useRef(null);
  const nodeRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(0);
  const readyRef = useRef(false);
  const playheadRef = useRef(0);
  const sourcesRef = useRef(new Set());
  const asstBufRef = useRef('');
  const gainRef = useRef(null);
  const micMutedRef = useRef(false);
  const spkMutedRef = useRef(false);
  const deliberateRef = useRef(false); // user asked to stop (vs socket drop)
  const reconnectsRef = useRef(0);
  const inputRateRef = useRef(24000);
  const onUserFinalRef = useRef(onUserFinal);
  const onAssistantFinalRef = useRef(onAssistantFinal);
  useEffect(() => { onUserFinalRef.current = onUserFinal; }, [onUserFinal]);
  useEffect(() => { onAssistantFinalRef.current = onAssistantFinal; }, [onAssistantFinal]);

  const flushPlayback = useCallback(() => {
    for (const src of sourcesRef.current) { try { src.stop(); } catch { /* noop */ } }
    sourcesRef.current.clear();
    if (ctxRef.current) playheadRef.current = ctxRef.current.currentTime;
  }, []);

  const playChunk = useCallback((int16) => {
    const ctx = ctxRef.current;
    if (!ctx || !int16.length) return;
    const f32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) f32[i] = int16[i] / 0x8000;
    const buffer = ctx.createBuffer(1, f32.length, OUTPUT_RATE);
    buffer.copyToChannel(f32, 0);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(gainRef.current || ctx.destination);
    const now = ctx.currentTime;
    const startAt = Math.max(now + 0.02, playheadRef.current);
    src.start(startAt);
    playheadRef.current = startAt + buffer.duration;
    sourcesRef.current.add(src);
    src.onended = () => sourcesRef.current.delete(src);
  }, []);

  const disconnect = useCallback(() => {
    deliberateRef.current = true;
    readyRef.current = false;
    cancelAnimationFrame(rafRef.current);
    flushPlayback();
    try { if (wsRef.current) wsRef.current.close(); } catch { /* noop */ }
    wsRef.current = null;
    try { if (nodeRef.current) { nodeRef.current.port.onmessage = null; nodeRef.current.disconnect(); } } catch { /* noop */ }
    nodeRef.current = null;
    try { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    streamRef.current = null;
    try { if (ctxRef.current) ctxRef.current.close(); } catch { /* noop */ }
    ctxRef.current = null;
    gainRef.current = null;
    setLevel(0);
    setMicMuted(false);
    micMutedRef.current = false;
    setStatus('idle');
  }, [flushPlayback]);

  // Opens (or re-opens) the relay socket. The mic/audio pipeline is untouched,
  // so the reconnect path reuses it — only the WS + token are fresh.
  const openSocket = useCallback((token) => {
    readyRef.current = false;
    const ws = new WebSocket(wsUrl(token));
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      const init = (getInit ? getInit() : {}) || {};
      const c = agentBus.getContext();
      ws.send(JSON.stringify({ type: 'init', persona: { id: 'copilot', voice: 'eve' }, inputRate: inputRateRef.current, pageName: c.pageName || init.pageName, movieName: c.movieName, clips: c.clips, presets: c.presets, gradeSummary: c.gradeSummary, ...init }));
    };

    ws.onmessage = (e) => {
      if (typeof e.data !== 'string') return;
      let m; try { m = JSON.parse(e.data); } catch { return; }
      switch (m.type) {
        case 'relay.ready':
          readyRef.current = true;
          reconnectsRef.current = 0;
          setError('');
          setStatus('live');
          break;
        case 'audio':
          playChunk(b64ToInt16(m.data));
          break;
        case 'interrupted':
          flushPlayback();
          break;
        case 'user_transcript':
          if (m.final) {
            setUserText('');
            if (m.text && onUserFinalRef.current) onUserFinalRef.current(m.text);
          } else {
            setUserText(m.text || '');
          }
          break;
        case 'assistant_transcript_delta':
          asstBufRef.current += m.text || '';
          setAssistantText(asstBufRef.current);
          break;
        case 'assistant_transcript':
          asstBufRef.current = '';
          setAssistantText('');
          if (m.text && onAssistantFinalRef.current) onAssistantFinalRef.current(m.text);
          break;
        case 'directive.tool_call':
          executeDirective(m.name, m.arguments);
          break;
        case 'error':
          setError(m.message || 'voice error');
          break;
        default:
          break;
      }
    };

    ws.onerror = () => { /* onclose carries the recovery logic */ };
    ws.onclose = () => {
      if (wsRef.current !== ws) return;
      const wasReady = readyRef.current;
      wsRef.current = null;
      readyRef.current = false;
      flushPlayback();
      if (deliberateRef.current) return; // disconnect() already cleaning up
      // Unexpected drop with the mic still live: retry with a fresh token.
      if (streamRef.current && reconnectsRef.current < MAX_RECONNECTS) {
        reconnectsRef.current += 1;
        setStatus('connecting');
        setTimeout(async () => {
          if (deliberateRef.current || !streamRef.current) return;
          try {
            openSocket(getToken ? await getToken() : '');
          } catch {
            setError('voice reconnect failed');
            disconnect();
          }
        }, 600 * reconnectsRef.current);
      } else {
        if (wasReady) setError('voice session ended');
        disconnect();
      }
    };
  }, [getToken, getInit, playChunk, flushPlayback, disconnect]);

  const connect = useCallback(async () => {
    if (!API) { setError('voice API not configured'); setStatus('error'); return; }
    deliberateRef.current = false;
    reconnectsRef.current = 0;
    setError(''); setStatus('connecting'); setUserText(''); setAssistantText('');
    try {
      const token = getToken ? await getToken() : '';
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      inputRateRef.current = Math.round(ctx.sampleRate); // native device rate (often 48k)
      playheadRef.current = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.value = spkMutedRef.current ? 0 : 1;
      gain.connect(ctx.destination);
      gainRef.current = gain;
      const blobUrl = URL.createObjectURL(new Blob([WORKLET_SRC], { type: 'application/javascript' }));
      await ctx.audioWorklet.addModule(blobUrl);
      URL.revokeObjectURL(blobUrl);

      const source = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, 'cap-processor');
      nodeRef.current = node;
      source.connect(node); // node not connected to destination → no echo

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;
      const meter = new Float32Array(analyser.fftSize);
      const tick = () => {
        analyser.getFloatTimeDomainData(meter);
        let sum = 0; for (let i = 0; i < meter.length; i++) sum += meter[i] * meter[i];
        setLevel(Math.min(1, Math.sqrt(sum / meter.length) * 3));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);

      node.port.onmessage = (e) => {
        if (micMutedRef.current) return; // muted: send nothing, not silence
        const ws = wsRef.current;
        if (readyRef.current && ws && ws.readyState === WebSocket.OPEN) ws.send(e.data);
      };

      openSocket(token);
    } catch (e) {
      setError(e.message || 'mic/connect failed');
      setStatus('error');
      disconnect();
    }
  }, [getToken, openSocket, disconnect]);

  const sendText = useCallback((text) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && text) ws.send(JSON.stringify({ type: 'text', text }));
  }, []);

  const toggleMicMuted = useCallback(() => {
    setMicMuted((m) => { micMutedRef.current = !m; return !m; });
  }, []);

  const toggleSpeakerMuted = useCallback(() => {
    setSpeakerMuted((m) => {
      spkMutedRef.current = !m;
      if (gainRef.current) gainRef.current.gain.value = !m ? 0 : 1;
      return !m;
    });
  }, []);

  useEffect(() => () => disconnect(), [disconnect]);

  return {
    status, error, level, userText, assistantText,
    connect, disconnect, sendText,
    micMuted, toggleMicMuted, speakerMuted, toggleSpeakerMuted,
    configured: !!API,
  };
}
